import dictionary from "../content/dictionary.json";
import topicsFallback from "../content/topics.json";
import lessonsFallback from "../content/lessons.json";
import timelineFallback from "../content/timeline.json";
import faqsFallback from "../content/faqs.json";
import quizzesFallback from "../content/quizzes.json";
import pathsFallback from "../content/paths.json";
import {
  fetchFullCurriculum,
  type ApiFaq,
  type ApiLesson,
  type ApiQuiz,
  type ApiTerm,
  type ApiTimelineEvent,
  type ApiTopic,
} from "../content/api";
import type { KnowledgePanel } from "./knowledge";
import { loadTermIndex } from "./knowledge";
import { track } from "../systems/analytics";
import {
  getProgress,
  isLessonComplete,
  markLessonComplete,
  pathProgress,
  setLastLesson,
  setLastPath,
} from "../systems/progress";
import { escapeHtml, readMinutes, renderBody } from "./md";
import { mountExplore } from "./explore";
import { mountAccess } from "./access";
import accessDirectory from "../content/access.json";
import {
  CLUB,
  CLUB_ROOMS,
  HOUSE_RULES,
  pathDisplayTitle,
  pathTicketLabel,
  roomById,
  type ClubRoomId,
} from "../brand/club";
import gsap from "gsap";

type ChapterId = string;
type Mode =
  | "paths"
  | "explore"
  | "access"
  | "lessons"
  | "timeline"
  | "faqs"
  | "terms";

const CHAPTER_ORDER = ["plant", "chemistry", "methods", "medical", "uk-law", "culture"];

type LearnSub = { mode: Mode; lesson?: string; path?: string; q?: string };

function parseLearnHash(): LearnSub {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const [pathPart, query = ""] = raw.split("?");
  const parts = pathPart.toLowerCase().split("/").filter(Boolean);
  const params = new URLSearchParams(query);
  const q = params.get("q") ?? undefined;
  if (parts[0] !== "learn") return { mode: "paths", q };
  const section = parts[1] as Mode | undefined;
  if (section === "lessons") return { mode: "lessons", lesson: parts[2], q };
  if (section === "paths") return { mode: "paths", path: parts[2], q };
  if (
    section === "timeline" ||
    section === "faqs" ||
    section === "terms" ||
    section === "explore" ||
    section === "access"
  ) {
    return { mode: section, q };
  }
  return { mode: "paths", q };
}

function writeLearnHash(sub: LearnSub, replace = false) {
  let next = "#/learn";
  if (sub.mode === "lessons" && sub.lesson) next = `#/learn/lessons/${sub.lesson}`;
  else if (sub.mode === "paths" && sub.path) next = `#/learn/paths/${sub.path}`;
  else if (sub.mode !== "paths") next = `#/learn/${sub.mode}`;
  else next = "#/learn";
  if (sub.q) next += `?q=${encodeURIComponent(sub.q)}`;
  if (window.location.hash === next) return;
  if (replace) window.history.replaceState(null, "", next);
  else window.location.hash = next;
}

function chapterLabel(id: string, topics: ApiTopic[]): string {
  return topics.find((t) => t.id === id)?.title ?? id;
}

function relatedTerms(lesson: ApiLesson, terms: ApiTerm[], limit = 8): ApiTerm[] {
  const hay = `${lesson.title} ${lesson.summary} ${lesson.body}`.toUpperCase();
  const scored = terms
    .map((t) => {
      const key = t.term.toUpperCase();
      if (key.length < 3) return { t, score: 0 };
      if (!hay.includes(key)) return { t, score: 0 };
      let score = key.length;
      if (lesson.chapter === t.chapter) score += 20;
      return { t, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  const out: ApiTerm[] = [];
  const seen = new Set<string>();
  for (const { t } of scored) {
    if (seen.has(t.term)) continue;
    seen.add(t.term);
    out.push(t);
    if (out.length >= limit) break;
  }
  return out;
}

export async function mountCurriculum(app: HTMLElement, panel: KnowledgePanel) {
  const remote = await fetchFullCurriculum();

  const terms: ApiTerm[] = remote?.terms?.length
    ? remote.terms
    : (dictionary.terms as ApiTerm[]);
  const topics: ApiTopic[] = remote?.topics?.length
    ? remote.topics
    : topicsFallback.topics.map((t, i) => ({
        id: t.id,
        title: t.title,
        summary: t.summary,
        sort_order: i + 1,
      }));
  const lessons: ApiLesson[] = remote?.lessons?.length
    ? remote.lessons
    : (lessonsFallback.lessons as ApiLesson[]);
  const timeline: ApiTimelineEvent[] = remote?.timeline?.length
    ? remote.timeline
    : (timelineFallback.timeline as ApiTimelineEvent[]);
  const faqs: ApiFaq[] = remote?.faqs?.length
    ? remote.faqs
    : (faqsFallback.faqs as ApiFaq[]);
  const quizzes: ApiQuiz[] = remote?.quizzes?.length
    ? remote.quizzes
    : (quizzesFallback.quizzes as ApiQuiz[]);
  const paths = pathsFallback.paths;

  loadTermIndex(terms);
  const sortedTerms = [...terms].sort((a, b) => a.term.localeCompare(b.term));
  const sortedLessons = [...lessons].sort((a, b) => {
    const ai = CHAPTER_ORDER.indexOf(a.chapter);
    const bi = CHAPTER_ORDER.indexOf(b.chapter);
    if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  const quizzesByLesson = new Map<string, ApiQuiz[]>();
  quizzes.forEach((q) => {
    const list = quizzesByLesson.get(q.lesson_slug) ?? [];
    list.push(q);
    quizzesByLesson.set(q.lesson_slug, list);
  });

  const initial = parseLearnHash();
  const progress = getProgress();

  app.innerHTML = `
    <div class="club" data-club>
      <div class="learn-stage"><canvas data-learn-canvas aria-hidden="true"></canvas></div>
      <div class="learn-page">
        <p class="club-lintel">
          <span class="club-lintel-mark">${escapeHtml(CLUB.lintel)}</span>
          <span class="club-lintel-rules">${escapeHtml(HOUSE_RULES[2])}</span>
        </p>
        <div class="learn-nav">
          <a class="learn-back" href="../">← Street door</a>
          <a class="learn-back learn-back--muted" href="#/anteroom">Anteroom</a>
          <span class="learn-progress" data-progress-label hidden></span>
        </div>
        <div class="club-header-row">
          <div class="club-brand-block">
            <h1 class="learn-title">cannac</h1>
            <p class="club-subtitle">${escapeHtml(CLUB.name)}</p>
            <p class="club-host" data-room-host></p>
            <label class="learn-omni">
              <span class="visually-hidden">Search the club</span>
              <input type="search" placeholder="Ask the house — lessons, FAQ, terms…" data-omni aria-label="Search the club" />
            </label>
          </div>
          <aside class="club-member-card" aria-label="Membership progress">
            <p class="club-member-kicker">Membership</p>
            <p class="club-member-name">Guest pass</p>
            <div class="club-resin" aria-hidden="true"><span class="club-resin-fill" data-resin-fill></span></div>
            <p class="club-member-meta" data-member-meta>0 / 0 folios stamped</p>
          </aside>
        </div>

        <div class="omni-results" data-omni-results hidden></div>

        <nav class="learn-modes" role="tablist" aria-label="Club rooms" data-modes></nav>

        <div class="learn-panel" data-panel="paths" hidden></div>
        <div class="learn-panel" data-panel="explore" hidden></div>
        <div class="learn-panel" data-panel="access" hidden></div>
        <div class="learn-panel" data-panel="lessons" hidden></div>
        <div class="learn-panel" data-panel="timeline" hidden></div>
        <div class="learn-panel" data-panel="faqs" hidden></div>
        <div class="learn-panel" data-panel="terms" hidden></div>
      </div>
    </div>
  `;

  const clubRoot = app.querySelector("[data-club]") as HTMLElement;
  const canvas = app.querySelector("[data-learn-canvas]") as HTMLCanvasElement;
  const modesEl = app.querySelector("[data-modes]") as HTMLElement;
  const progressLabel = app.querySelector("[data-progress-label]") as HTMLElement;
  const memberMeta = app.querySelector("[data-member-meta]") as HTMLElement;
  const resinFill = app.querySelector("[data-resin-fill]") as HTMLElement;
  const roomHost = app.querySelector("[data-room-host]") as HTMLElement;
  const omni = app.querySelector("[data-omni]") as HTMLInputElement;
  const omniResults = app.querySelector("[data-omni-results]") as HTMLElement;

  const panelEls: Record<Mode, HTMLElement> = {
    paths: app.querySelector('[data-panel="paths"]') as HTMLElement,
    explore: app.querySelector('[data-panel="explore"]') as HTMLElement,
    access: app.querySelector('[data-panel="access"]') as HTMLElement,
    lessons: app.querySelector('[data-panel="lessons"]') as HTMLElement,
    timeline: app.querySelector('[data-panel="timeline"]') as HTMLElement,
    faqs: app.querySelector('[data-panel="faqs"]') as HTMLElement,
    terms: app.querySelector('[data-panel="terms"]') as HTMLElement,
  };

  let mode: Mode = initial.mode;
  let activePathId = initial.path || progress.lastPath || paths[0]?.id || "";
  let activeLessonSlug =
    initial.lesson || progress.lastLesson || sortedLessons[0]?.slug || "";
  let pathFocusSlugs: string[] | null = null;
  const modeBtns: HTMLButtonElement[] = [];

  const refreshProgress = () => {
    const p = getProgress();
    const total = sortedLessons.length;
    const done = p.completed.length;
    progressLabel.textContent = `${done}/${total} lessons`;
    memberMeta.textContent = `${done} / ${total} folios stamped`;
    const pct = total ? Math.round((done / total) * 100) : 0;
    resinFill.style.width = `${pct}%`;
    const nameEl = clubRoot.querySelector(".club-member-name");
    if (nameEl) {
      nameEl.textContent =
        done === 0
          ? "Guest pass"
          : done >= total
            ? "Founding member"
            : done >= Math.ceil(total * 0.5)
              ? "Regular"
              : "Member";
    }
  };

  const setRoomChrome = (next: Mode) => {
    const room = roomById(next as ClubRoomId);
    roomHost.textContent = room.host;
    document.title = `${CLUB.name} | ${room.name}`;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (!reduce) {
      gsap.fromTo(
        panelEls[next],
        { autoAlpha: 0.6, y: 10 },
        { autoAlpha: 1, y: 0, duration: 0.35, ease: "power2.out", overwrite: true },
      );
    }
  };

  let openLessonRef: ((slug: string, opts?: { fromPath?: boolean }) => void) | null =
    null;

  const setMode = (next: Mode, opts?: { lesson?: string; path?: string; silent?: boolean }) => {
    mode = next;
    if (opts?.lesson) activeLessonSlug = opts.lesson;
    if (opts?.path) activePathId = opts.path;
    modeBtns.forEach((b) => {
      b.setAttribute("aria-selected", b.dataset.mode === mode ? "true" : "false");
    });
    (Object.keys(panelEls) as Mode[]).forEach((m) => {
      panelEls[m].hidden = m !== mode;
    });
    setRoomChrome(mode);
    if (!opts?.silent) {
      writeLearnHash({
        mode,
        lesson: mode === "lessons" ? activeLessonSlug : undefined,
        path: mode === "paths" ? activePathId : undefined,
      });
      track("curriculum_mode", { mode });
    }
    if (mode === "lessons" && openLessonRef && activeLessonSlug) {
      openLessonRef(activeLessonSlug);
    }
  };

  CLUB_ROOMS.forEach((room) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "mode-pill";
    btn.dataset.mode = room.id;
    btn.innerHTML = `<span class="mode-pill-door">${escapeHtml(room.door)}</span><span class="mode-pill-short">${escapeHtml(room.short)}</span>`;
    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-label", room.name);
    btn.addEventListener("click", () => setMode(room.id));
    modesEl.appendChild(btn);
    modeBtns.push(btn);
  });

  // —— Omnisearch ——
  const runOmni = (qRaw: string) => {
    const q = qRaw.trim().toLowerCase();
    if (q.length < 2) {
      omniResults.hidden = true;
      omniResults.innerHTML = "";
      return;
    }
    type Hit = { kind: string; title: string; meta: string; go: () => void };
    const hits: Hit[] = [];

    sortedLessons.forEach((l) => {
      if (
        l.title.toLowerCase().includes(q) ||
        l.summary.toLowerCase().includes(q) ||
        l.body.toLowerCase().includes(q)
      ) {
        hits.push({
          kind: "Lesson",
          title: l.title,
          meta: chapterLabel(l.chapter, topics),
          go: () => {
            pathFocusSlugs = null;
            setMode("lessons", { lesson: l.slug });
            openLessonRef?.(l.slug);
            omni.value = "";
            omniResults.hidden = true;
          },
        });
      }
    });
    faqs.forEach((f) => {
      if (f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)) {
        hits.push({
          kind: "FAQ",
          title: f.question,
          meta: chapterLabel(f.chapter, topics),
          go: () => {
            setMode("faqs");
            omni.value = "";
            omniResults.hidden = true;
            const faqSearch = panelEls.faqs.querySelector(
              "[data-faq-search]",
            ) as HTMLInputElement | null;
            if (faqSearch) {
              faqSearch.value = qRaw;
              faqSearch.dispatchEvent(new Event("input"));
            }
          },
        });
      }
    });
    sortedTerms.forEach((t) => {
      if (
        t.term.toLowerCase().includes(q) ||
        t.plain.toLowerCase().includes(q) ||
        t.clear.toLowerCase().includes(q)
      ) {
        hits.push({
          kind: "Term",
          title: t.term,
          meta: t.chapter,
          go: () => {
            setMode("terms");
            panel.open(t.term);
            omni.value = "";
            omniResults.hidden = true;
          },
        });
      }
    });
    timeline.forEach((ev) => {
      if (
        ev.title.toLowerCase().includes(q) ||
        ev.summary.toLowerCase().includes(q) ||
        ev.year.toLowerCase().includes(q)
      ) {
        hits.push({
          kind: "Timeline",
          title: `${ev.year} — ${ev.title}`,
          meta: ev.tag || "UK policy",
          go: () => {
            setMode("timeline");
            omni.value = "";
            omniResults.hidden = true;
          },
        });
      }
    });
    accessDirectory.clinics.forEach((c) => {
      if (
        c.name.toLowerCase().includes(q) ||
        c.summary.toLowerCase().includes(q) ||
        c.focus.toLowerCase().includes(q)
      ) {
        hits.push({
          kind: "Clinic",
          title: c.name,
          meta: "Clinic Annex",
          go: () => {
            setMode("access");
            omni.value = "";
            omniResults.hidden = true;
          },
        });
      }
    });
    accessDirectory.pharmacies.forEach((p) => {
      if (p.name.toLowerCase().includes(q) || p.summary.toLowerCase().includes(q)) {
        hits.push({
          kind: "Pharmacy",
          title: p.name,
          meta: "Clinic Annex",
          go: () => {
            setMode("access");
            omni.value = "";
            omniResults.hidden = true;
          },
        });
      }
    });

    omniResults.innerHTML = "";
    hits.slice(0, 12).forEach((h) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "omni-hit";
      btn.innerHTML = `<span class="omni-kind">${escapeHtml(h.kind)}</span><span class="omni-title">${escapeHtml(h.title)}</span><span class="omni-meta">${escapeHtml(h.meta)}</span>`;
      btn.addEventListener("click", h.go);
      omniResults.appendChild(btn);
    });
    if (!hits.length) {
      omniResults.innerHTML = `<p class="learn-empty">Nothing matched “${escapeHtml(qRaw)}”.</p>`;
    }
    omniResults.hidden = false;
  };

  omni.addEventListener("input", () => runOmni(omni.value));
  omni.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      omniResults.hidden = true;
      omni.blur();
    }
  });
  if (initial.q) {
    omni.value = initial.q;
    runOmni(initial.q);
  }

  // —— Paths ——
  {
    const host = panelEls.paths;
    const renderPaths = () => {
      const p = getProgress();
      const cloak = roomById("paths");
      host.innerHTML = `
        <div class="club-room-banner">
          <h2>${escapeHtml(cloak.name)}</h2>
          <p>${escapeHtml(cloak.host)}</p>
        </div>
        <div class="paths-hero">
          <p class="paths-kicker">Coat check</p>
          <p class="paths-lead">Claim a tour tag. The Founders’ Tour walks every room. House rule: ${escapeHtml(HOUSE_RULES[0])}</p>
          <div class="paths-cta-row">
          ${
            p.lastLesson
              ? `<button type="button" class="path-continue" data-continue>Resume folio: ${escapeHtml(
                  sortedLessons.find((l) => l.slug === p.lastLesson)?.title || p.lastLesson,
                )}</button>`
              : ""
          }
          <button type="button" class="path-continue path-continue--ghost" data-to-explore>To the Tasting Bar</button>
          </div>
        </div>
        <div class="paths-grid" data-paths-grid></div>
        <div class="chapter-strip" data-chapters></div>
      `;

      host.querySelector("[data-continue]")?.addEventListener("click", () => {
        if (!p.lastLesson) return;
        pathFocusSlugs = null;
        setMode("lessons", { lesson: p.lastLesson });
        openLessonRef?.(p.lastLesson);
      });
      host.querySelector("[data-to-explore]")?.addEventListener("click", () => {
        setMode("explore");
      });

      const grid = host.querySelector("[data-paths-grid]") as HTMLElement;
      paths.forEach((path) => {
        const { done, total } = pathProgress(path.lessons);
        const card = document.createElement("button");
        card.type = "button";
        card.className = "path-card";
        card.dataset.ticket = pathTicketLabel(path.id);
        card.setAttribute("aria-current", path.id === activePathId ? "true" : "false");
        card.innerHTML = `
          <span class="path-card-title">${escapeHtml(pathDisplayTitle(path.id, path.title))}</span>
          <span class="path-card-sum">${escapeHtml(path.summary)}</span>
          <span class="path-card-bar"><span style="width:${total ? (done / total) * 100 : 0}%"></span></span>
          <span class="path-card-meta">${done}/${total} stamped · ${path.lessons.length} stops</span>
        `;
        card.addEventListener("click", () => {
          activePathId = path.id;
          setLastPath(path.id);
          pathFocusSlugs = [...path.lessons];
          const next =
            path.lessons.find((s) => !isLessonComplete(s)) || path.lessons[0];
          setMode("lessons", { lesson: next, path: path.id });
          openLessonRef?.(next, { fromPath: true });
          track("path_open", { path: path.id });
        });
        grid.appendChild(card);
      });

      const strip = host.querySelector("[data-chapters]") as HTMLElement;
      strip.innerHTML = `<p class="paths-kicker">Browse by wing</p>`;
      [...topics]
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .forEach((t) => {
          const count = sortedLessons.filter((l) => l.chapter === t.id).length;
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "chapter-chip";
          btn.innerHTML = `<strong>${escapeHtml(t.title)}</strong><span>${count} folios</span><em>${escapeHtml(t.summary || "")}</em>`;
          btn.addEventListener("click", () => {
            pathFocusSlugs = null;
            setMode("lessons");
            const filterBtn = [
              ...panelEls.lessons.querySelectorAll(".filter-pill"),
            ].find((el) => el.textContent === t.title) as HTMLButtonElement | undefined;
            filterBtn?.click();
          });
          strip.appendChild(btn);
        });
    };
    renderPaths();
    (panelEls.paths as HTMLElement & { _render?: () => void })._render = renderPaths;
  }

  // —— Explore ——
  mountExplore(panelEls.explore);

  // —— Clinic Annex ——
  mountAccess(panelEls.access);

  // —— Lessons ——
  {
    const host = panelEls.lessons;
    host.innerHTML = `
      <div class="club-room-banner">
        <h2>Library</h2>
        <p>Pull a spine. Read Plain, Clear, or Deep. Stamp the folio when you’re done — membership fills with resin.</p>
      </div>
      <div class="learn-toolbar">
        <div class="learn-filters" data-lesson-filters role="tablist" aria-label="Filter lessons by topic"></div>
      </div>
      <div class="lesson-layout">
        <div class="lesson-list" data-lesson-list role="list"></div>
        <article class="lesson-reader" data-lesson-reader></article>
      </div>
    `;
    const filters = host.querySelector("[data-lesson-filters]") as HTMLElement;
    const list = host.querySelector("[data-lesson-list]") as HTMLElement;
    const reader = host.querySelector("[data-lesson-reader]") as HTMLElement;
    let activeChapter: ChapterId = "all";

    const openLesson = (slug: string) => {
      const lesson = sortedLessons.find((l) => l.slug === slug);
      if (!lesson) return;
      activeLessonSlug = slug;
      setLastLesson(slug);
      writeLearnHash({ mode: "lessons", lesson: slug }, true);
      list.querySelectorAll(".lesson-card").forEach((el) => {
        el.setAttribute(
          "aria-current",
          el.getAttribute("data-slug") === slug ? "true" : "false",
        );
      });

      const related = relatedTerms(lesson, sortedTerms);
      const lessonQuizzes = quizzesByLesson.get(slug) ?? [];
      // When a path is focused, prev/next follow path order — not global chapter sort.
      const navList = pathFocusSlugs
        ? (pathFocusSlugs
            .map((s) => sortedLessons.find((l) => l.slug === s))
            .filter(Boolean) as ApiLesson[])
        : sortedLessons;
      const idx = navList.findIndex((l) => l.slug === slug);
      const prev = idx > 0 ? navList[idx - 1] : undefined;
      const next = idx >= 0 && idx < navList.length - 1 ? navList[idx + 1] : undefined;
      const done = isLessonComplete(slug);

      reader.innerHTML = `
        <div class="lesson-reader-top" data-chapter="${escapeHtml(lesson.chapter)}">
          <p class="lesson-kicker">${escapeHtml(chapterLabel(lesson.chapter, topics))} wing</p>
          <p class="lesson-meta">${readMinutes(lesson.body)} min · folio${done ? " · stamped" : ""}</p>
        </div>
        <h2 class="lesson-title">${escapeHtml(lesson.title)}</h2>
        <p class="lesson-lead">${escapeHtml(lesson.summary)}</p>
        <div class="lesson-body">${renderBody(lesson.body)}</div>
        ${
          related.length
            ? `<div class="related-terms"><p class="paths-kicker">From the cellar</p><div class="related-row" data-related></div></div>`
            : ""
        }
        ${
          lessonQuizzes.length
            ? `<div class="lesson-quiz" data-quiz></div>`
            : ""
        }
        <div class="lesson-actions">
          <button type="button" class="lesson-complete ${done ? "is-done" : ""}" data-complete>
            ${done ? "Stamped" : "Stamp folio"}
          </button>
          ${
            lesson.source
              ? `<a class="lesson-source" href="${escapeHtml(lesson.source)}" target="_blank" rel="noopener noreferrer">House source</a>`
              : ""
          }
        </div>
        <div class="lesson-nav">
          <button type="button" data-prev ${prev ? "" : "disabled"}>${prev ? `← ${escapeHtml(prev.title)}` : "←"}</button>
          <button type="button" data-next ${next ? "" : "disabled"}>${next ? `${escapeHtml(next.title)} →` : "→"}</button>
        </div>
      `;

      clubRoot.dataset.chapter = lesson.chapter;
      window.dispatchEvent(
        new CustomEvent("cannac:chapter", { detail: { chapter: lesson.chapter } }),
      );

      reader.querySelectorAll("[data-related]").forEach((row) => {
        related.forEach((t) => {
          const chip = document.createElement("button");
          chip.type = "button";
          chip.className = "related-chip";
          chip.textContent = t.term;
          chip.addEventListener("click", () => panel.open(t.term));
          row.appendChild(chip);
        });
      });

      const quizHost = reader.querySelector("[data-quiz]") as HTMLElement | null;
      if (quizHost && lessonQuizzes.length) {
        quizHost.innerHTML = `<p class="paths-kicker">Concierge quiz</p>`;
        lessonQuizzes.forEach((qz, qi) => {
          const block = document.createElement("div");
          block.className = "quiz-block";
          block.innerHTML = `<p class="quiz-q">${escapeHtml(qz.question)}</p>`;
          const choices = document.createElement("div");
          choices.className = "quiz-choices";
          qz.choices.forEach((c, ci) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "quiz-choice";
            btn.textContent = c;
            btn.addEventListener("click", () => {
              const correct = ci === qz.answer_index;
              choices.querySelectorAll("button").forEach((b, bi) => {
                b.disabled = true;
                if (bi === qz.answer_index) b.classList.add("is-correct");
                if (bi === ci && !correct) b.classList.add("is-wrong");
              });
              const note = document.createElement("p");
              note.className = "quiz-explain";
              note.textContent = qz.explain || (correct ? "Stamped correct." : "Not quite — try the folio again.");
              block.appendChild(note);
              track("quiz_answer", { slug, qi, correct: correct ? 1 : 0 });
            });
            choices.appendChild(btn);
          });
          block.appendChild(choices);
          quizHost.appendChild(block);
        });
      }

      reader.querySelector("[data-complete]")?.addEventListener("click", () => {
        markLessonComplete(slug);
        refreshProgress();
        (panelEls.paths as HTMLElement & { _render?: () => void })._render?.();
        openLesson(slug);
        track("lesson_complete", { slug });
      });
      reader.querySelector("[data-prev]")?.addEventListener("click", () => {
        if (prev) openLesson(prev.slug);
      });
      reader.querySelector("[data-next]")?.addEventListener("click", () => {
        if (next) openLesson(next.slug);
      });

      track("lesson_open", { slug: lesson.slug, chapter: lesson.chapter });
      refreshProgress();
    };
    openLessonRef = openLesson;

    const renderList = () => {
      list.innerHTML = "";
      let filtered = sortedLessons.filter(
        (l) => activeChapter === "all" || l.chapter === activeChapter,
      );
      if (pathFocusSlugs) {
        filtered = pathFocusSlugs
          .map((s) => sortedLessons.find((l) => l.slug === s))
          .filter(Boolean) as ApiLesson[];
      }
      filtered.forEach((l) => {
        const card = document.createElement("button");
        card.type = "button";
        card.className = "lesson-card";
        if (isLessonComplete(l.slug)) card.classList.add("is-complete");
        card.setAttribute("role", "listitem");
        card.dataset.slug = l.slug;
        card.dataset.chapter = l.chapter;
        card.innerHTML = `
          <span class="lesson-card-meta">${escapeHtml(chapterLabel(l.chapter, topics))}${isLessonComplete(l.slug) ? " · stamped" : ""}</span>
          <span class="lesson-card-title">${escapeHtml(l.title)}</span>
          <span class="lesson-card-sum">${escapeHtml(l.summary)}</span>
        `;
        card.addEventListener("click", () => openLesson(l.slug));
        list.appendChild(card);
      });
      if (!filtered.length) {
        list.innerHTML = `<p class="learn-empty">No folios in this wing.</p>`;
      }
      const pick =
        filtered.find((l) => l.slug === activeLessonSlug) ?? filtered[0];
      if (pick) openLesson(pick.slug);
    };

    const filterBtns: HTMLButtonElement[] = [];
    const makeFilter = (id: ChapterId, label: string) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "filter-pill";
      btn.textContent = label;
      btn.setAttribute("aria-selected", id === "all" ? "true" : "false");
      btn.addEventListener("click", () => {
        activeChapter = id;
        pathFocusSlugs = null;
        filterBtns.forEach((b) =>
          b.setAttribute("aria-selected", b === btn ? "true" : "false"),
        );
        renderList();
      });
      filters.appendChild(btn);
      filterBtns.push(btn);
    };
    makeFilter("all", "All");
    [...topics]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((t) => makeFilter(t.id, t.title));

    renderList();
  }

  // —— Timeline ——
  {
    const host = panelEls.timeline;
    const wall = roomById("timeline");
    host.innerHTML = `
      <div class="club-room-banner">
        <h2>${escapeHtml(wall.name)}</h2>
        <p>${escapeHtml(wall.host)}</p>
      </div>
      <div class="timeline-list" data-timeline></div>
    `;
    const ol = host.querySelector("[data-timeline]") as HTMLElement;
    [...timeline]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((ev) => {
        const li = document.createElement("article");
        li.className = "timeline-item";
        li.innerHTML = `
          <div class="timeline-year">${escapeHtml(ev.year)}</div>
          <h3 class="timeline-title">${escapeHtml(ev.title)}</h3>
          <p class="timeline-summary">${escapeHtml(ev.summary)}</p>
          ${ev.tag ? `<span class="timeline-tag">${escapeHtml(ev.tag)}</span>` : ""}
        `;
        ol.appendChild(li);
      });
  }

  // —— FAQs ——
  {
    const host = panelEls.faqs;
    const desk = roomById("faqs");
    host.innerHTML = `
      <div class="club-room-banner">
        <h2>${escapeHtml(desk.name)}</h2>
        <p>${escapeHtml(desk.host)}</p>
      </div>
      <div class="learn-toolbar">
        <input class="learn-search" type="search" placeholder="Ask the desk…" aria-label="Search FAQ" data-faq-search />
        <div class="learn-filters" data-faq-filters></div>
      </div>
      <div class="faq-list" data-faq-list></div>
    `;
    const list = host.querySelector("[data-faq-list]") as HTMLElement;
    const search = host.querySelector("[data-faq-search]") as HTMLInputElement;
    const filters = host.querySelector("[data-faq-filters]") as HTMLElement;
    let activeChapter: ChapterId = "all";
    let query = "";

    const render = () => {
      list.innerHTML = "";
      const q = query.trim().toLowerCase();
      const filtered = faqs.filter((f) => {
        if (activeChapter !== "all" && f.chapter !== activeChapter) return false;
        if (!q) return true;
        return (
          f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
        );
      });
      filtered.forEach((f) => {
        const details = document.createElement("details");
        details.className = "faq-item";
        details.innerHTML = `
          <summary class="faq-q">${escapeHtml(f.question)}</summary>
          <p class="faq-a">${escapeHtml(f.answer)}</p>
          <span class="faq-meta">${escapeHtml(chapterLabel(f.chapter, topics))}</span>
        `;
        details.addEventListener("toggle", () => {
          details.classList.toggle("is-open", details.open);
        });
        list.appendChild(details);
      });
      if (!filtered.length) {
        list.innerHTML = `<p class="learn-empty">The desk has nothing on that.</p>`;
      }
    };

    const filterBtns: HTMLButtonElement[] = [];
    const makeFilter = (id: ChapterId, label: string) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "filter-pill";
      btn.textContent = label;
      btn.setAttribute("aria-selected", id === "all" ? "true" : "false");
      btn.addEventListener("click", () => {
        activeChapter = id;
        filterBtns.forEach((b) =>
          b.setAttribute("aria-selected", b === btn ? "true" : "false"),
        );
        render();
      });
      filters.appendChild(btn);
      filterBtns.push(btn);
    };
    makeFilter("all", "All");
    [...topics]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((t) => makeFilter(t.id, t.title));

    search.addEventListener("input", () => {
      query = search.value;
      render();
    });
    render();
  }

  // —— Terms ——
  {
    const host = panelEls.terms;
    const cellar = roomById("terms");
    host.innerHTML = `
      <div class="club-room-banner">
        <h2>${escapeHtml(cellar.name)}</h2>
        <p>${escapeHtml(cellar.host)}</p>
      </div>
      <div class="learn-toolbar">
        <input class="learn-search" type="search" placeholder="Search the bins…" aria-label="Search cannabis terms" data-term-search />
        <div class="learn-filters" data-term-filters role="tablist" aria-label="Filter by topic"></div>
      </div>
      <div class="terms-az learn-letters" data-letters aria-label="Filter by letter"></div>
      <div class="learn-grid" role="list" data-term-grid></div>
    `;

    const grid = host.querySelector("[data-term-grid]") as HTMLElement;
    const search = host.querySelector("[data-term-search]") as HTMLInputElement;
    const filters = host.querySelector("[data-term-filters]") as HTMLElement;
    const lettersEl = host.querySelector("[data-letters]") as HTMLElement;

    let activeChapter: ChapterId = "all";
    let query = "";
    let activeLetter = "ALL";

    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const present = new Set(
      sortedTerms.map((t) => {
        const ch = t.term.replace(/^[^A-Z0-9]+/i, "")[0]?.toUpperCase() || "";
        return /[A-Z]/.test(ch) ? ch : "#";
      }),
    );
    const letterBtns: HTMLButtonElement[] = [];

    const makeLetter = (id: string, label: string, enabled = true) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "letter-pill";
      btn.textContent = label;
      btn.disabled = !enabled;
      btn.setAttribute("aria-pressed", id === "ALL" ? "true" : "false");
      btn.setAttribute("aria-current", id === "ALL" ? "true" : "false");
      btn.addEventListener("click", () => {
        activeLetter = id;
        letterBtns.forEach((b) => {
          const on = b === btn;
          b.setAttribute("aria-pressed", on ? "true" : "false");
          b.setAttribute("aria-current", on ? "true" : "false");
        });
        render();
      });
      lettersEl.appendChild(btn);
      letterBtns.push(btn);
    };

    makeLetter("ALL", "All");
    makeLetter("#", "#", present.has("#"));
    alphabet.forEach((L) => makeLetter(L, L, present.has(L)));

    const filterButtons: HTMLButtonElement[] = [];
    const makeFilter = (id: ChapterId, label: string) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "filter-pill";
      btn.textContent = label;
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", id === "all" ? "true" : "false");
      btn.addEventListener("click", () => {
        activeChapter = id;
        filterButtons.forEach((b) =>
          b.setAttribute("aria-selected", b === btn ? "true" : "false"),
        );
        render();
      });
      filters.appendChild(btn);
      filterButtons.push(btn);
    };

    makeFilter("all", "All");
    [...topics]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((t) => makeFilter(t.id, t.title));

    const render = () => {
      grid.innerHTML = "";
      const q = query.trim().toUpperCase();
      const filtered = sortedTerms.filter((t) => {
        if (activeChapter !== "all" && t.chapter !== activeChapter) return false;
        if (activeLetter === "#") {
          if (/^[A-Z]/.test(t.term.replace(/^[^A-Z0-9]+/i, "").toUpperCase())) return false;
        } else if (activeLetter !== "ALL") {
          const first = t.term.replace(/^[^A-Z0-9]+/i, "")[0]?.toUpperCase();
          if (first !== activeLetter) return false;
        }
        if (!q) return true;
        return (
          t.term.includes(q) ||
          t.plain.toUpperCase().includes(q) ||
          t.clear.toUpperCase().includes(q)
        );
      });

      filtered.forEach((t) => {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "term-row term-chip";
        chip.setAttribute("role", "listitem");
        chip.innerHTML = `<strong class="term-chip-label">${escapeHtml(t.term)}</strong><span class="term-chip-meta">${escapeHtml(t.chapter)}</span>`;
        chip.addEventListener("click", () => panel.open(t.term));
        grid.appendChild(chip);
      });

      if (!filtered.length) {
        const empty = document.createElement("p");
        empty.className = "learn-empty";
        empty.textContent = "Empty bin.";
        grid.appendChild(empty);
      }
    };

    search.addEventListener("input", () => {
      query = search.value;
      render();
    });
    render();
  }

  // Avoid remount loops: only respond to learn↔anteroom at app level;
  // within learn, sync from hash if user uses back/forward.
  const onHash = () => {
    const sub = parseLearnHash();
    if (sub.mode !== mode || (sub.lesson && sub.lesson !== activeLessonSlug)) {
      if (sub.path) {
        activePathId = sub.path;
        const path = paths.find((p) => p.id === sub.path);
        if (path) pathFocusSlugs = [...path.lessons];
      }
      setMode(sub.mode, { lesson: sub.lesson, path: sub.path, silent: true });
      if (sub.mode === "lessons" && sub.lesson) openLessonRef?.(sub.lesson);
    }
  };
  window.addEventListener("hashchange", onHash);

  refreshProgress();
  setMode(initial.mode, {
    lesson: initial.lesson,
    path: initial.path,
    silent: true,
  });
  if (initial.mode === "lessons" && initial.lesson) {
    openLessonRef?.(initial.lesson);
  }
  writeLearnHash(
    {
      mode: initial.mode,
      lesson: initial.mode === "lessons" ? activeLessonSlug : undefined,
      path: initial.mode === "paths" ? activePathId : undefined,
      q: initial.q,
    },
    true,
  );

  track("curriculum_view", {
    source: remote?.ok ? "db" : "bundle",
    lessons: sortedLessons.length,
    terms: sortedTerms.length,
  });

  return {
    canvas,
    destroy: () => window.removeEventListener("hashchange", onHash),
  };
}
